import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ParchmentCard from '@/components/ui/ParchmentCard';
import ThemeToggle from '@/components/ui/ThemeToggle';
import useAuthStore from '@/store/authStore';
import * as playerService from '@/services/playerService';
import usePushNotifications from '@/hooks/usePushNotifications';

const SETTINGS_KEY = 'hakken-game-settings';

const loadSettings = () => {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const saveSettings = (settings) => {
  localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
};

function ToggleRow({ label, description, value, onChange }) {
  return (
    <div className="flex items-center justify-between gap-3 py-3">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-bold text-fg">{label}</p>
        {description && <p className="mt-0.5 text-xs text-fg/55">{description}</p>}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={value}
        onClick={() => onChange(!value)}
        className={`relative h-7 w-12 flex-shrink-0 rounded-full transition-colors duration-200 ${value ? 'bg-ocean' : 'bg-surfaceAlt'}`}
      >
        <span
          className={`absolute top-0.5 h-6 w-6 rounded-full bg-white shadow-sm transition-transform duration-200 ${value ? 'translate-x-5' : 'translate-x-0.5'}`}
        />
      </button>
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <h2 className="mb-2 mt-5 px-4 text-xs font-black uppercase tracking-widest text-accent/70">
      {children}
    </h2>
  );
}

export default function GameSettingsPage() {
  const navigate = useNavigate();
  const logout = useAuthStore((state) => state.logout);

  const push = usePushNotifications();

  const stored = loadSettings();
  const [location, setLocation] = useState(stored.location ?? true);
  const [orientation, setOrientation] = useState(stored.orientation ?? true);
  const [movement, setMovement] = useState(stored.movement ?? true);

  const [deleteStep, setDeleteStep] = useState('idle'); // idle | confirm | deleting | done
  const [deleteError, setDeleteError] = useState('');

  const handleToggle = (key, setter) => (val) => {
    setter(val);
    saveSettings({ ...loadSettings(), [key]: val });
  };

  const handleDeleteAccount = async () => {
    if (deleteStep === 'idle') {
      setDeleteStep('confirm');
      return;
    }
    setDeleteStep('deleting');
    setDeleteError('');
    try {
      await playerService.deleteAccount();
      setDeleteStep('done');
      logout();
      setTimeout(() => navigate('/', { replace: true }), 1500);
    } catch (err) {
      setDeleteError(err.response?.data?.message || 'Não foi possível eliminar a conta. Tenta novamente.');
      setDeleteStep('confirm');
    }
  };

  if (deleteStep === 'done') {
    return (
      <main className="h-full overflow-y-auto bg-background flex flex-col items-center justify-center px-4">
        <div className="text-center">
          <p className="text-4xl mb-3">🗑️</p>
          <p className="font-serif text-xl font-black text-accent">Conta eliminada</p>
          <p className="mt-2 text-sm text-muted">A redirecionar...</p>
        </div>
      </main>
    );
  }

  return (
    <main className="h-full overflow-y-auto bg-background pb-8">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 pt-6 pb-4">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="flex h-9 w-9 items-center justify-center rounded-full text-muted hover:text-fg transition-colors"
          style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)' }}
          aria-label="Voltar"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
        <h1 className="font-serif text-2xl font-black tracking-wide text-accent">Definições</h1>
      </div>

      {/* Aparência */}
      <SectionTitle>Aparência</SectionTitle>
      <div className="px-4">
        <ThemeToggle variant="full" />
      </div>

      {/* Localização */}
      <SectionTitle>Localização</SectionTitle>
      <div className="px-4">
        <ParchmentCard className="text-fg divide-y divide-edge">
          <ToggleRow
            label="Usar localização GPS"
            description="Permite ao jogo detectar onde estás para descobrires POIs próximos."
            value={location}
            onChange={handleToggle('location', setLocation)}
          />
        </ParchmentCard>
      </div>

      {/* Orientação */}
      <SectionTitle>Orientação</SectionTitle>
      <div className="px-4">
        <ParchmentCard className="text-fg divide-y divide-edge">
          <ToggleRow
            label="Bússola / orientação do dispositivo"
            description="Roda o mapa de acordo com a direcção para onde estás virado."
            value={orientation}
            onChange={handleToggle('orientation', setOrientation)}
          />
        </ParchmentCard>
      </div>

      {/* Movimento */}
      <SectionTitle>Movimento</SectionTitle>
      <div className="px-4">
        <ParchmentCard className="text-fg divide-y divide-edge">
          <ToggleRow
            label="Seguir o meu movimento"
            description="O mapa centra-se automaticamente enquanto caminhes."
            value={movement}
            onChange={handleToggle('movement', setMovement)}
          />
        </ParchmentCard>
      </div>

      {/* Notificações */}
      {push.supported && (
        <>
          <SectionTitle>Notificações</SectionTitle>
          <div className="px-4">
            <ParchmentCard className="text-fg divide-y divide-edge">
              <ToggleRow
                label="Notificações push"
                description={push.status === 'denied'
                  ? 'Bloqueadas pelo navegador. Ativa-as nas definições do dispositivo.'
                  : 'Recebe avisos de missões concluídas, medalhas, pedidos de amizade e ofertas.'}
                value={push.status === 'granted' && push.subscribed}
                onChange={(val) => (val ? push.subscribe() : push.unsubscribe())}
              />
            </ParchmentCard>
          </div>
        </>
      )}

      {/* Zona de perigo */}
      <SectionTitle>Zona de perigo</SectionTitle>
      <div className="px-4">
        <ParchmentCard className="text-fg">
          {deleteStep === 'idle' && (
            <div className="py-1">
              <p className="text-sm font-bold text-fg">Eliminar conta</p>
              <p className="mt-1 text-xs text-fg/55">
                Apaga permanentemente o teu perfil, progresso, medalhas e todos os dados associados. Esta acção é irreversível.
              </p>
              <button
                type="button"
                onClick={() => setDeleteStep('confirm')}
                className="mt-3 w-full rounded-xl py-2.5 text-sm font-black text-red-600 transition-colors hover:bg-red-50 active:scale-95"
                style={{ border: '1.5px solid rgba(220,38,38,0.3)' }}
              >
                Eliminar conta
              </button>
            </div>
          )}

          {deleteStep === 'confirm' && (
            <div className="py-1">
              <div className="rounded-xl bg-red-50 border border-red-200 p-3 mb-3">
                <p className="text-sm font-black text-red-700">Tens a certeza?</p>
                <p className="mt-1 text-xs text-red-600">
                  Todos os teus dados serão eliminados permanentemente. Não podes desfazer esta acção.
                </p>
              </div>
              {deleteError && (
                <p className="mb-2 text-xs font-bold text-red-600 text-center">{deleteError}</p>
              )}
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => { setDeleteStep('idle'); setDeleteError(''); }}
                  className="flex-1 rounded-xl py-2.5 text-sm font-bold text-fg/60 transition-colors hover:bg-surfaceAlt"
                  style={{ border: '1px solid rgba(0,0,0,0.12)' }}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleDeleteAccount}
                  className="flex-1 rounded-xl bg-red-600 py-2.5 text-sm font-black text-white transition-colors hover:bg-red-700 active:scale-95"
                >
                  Sim, eliminar
                </button>
              </div>
            </div>
          )}

          {deleteStep === 'deleting' && (
            <div className="py-4 flex flex-col items-center gap-2">
              <div className="h-8 w-8 animate-spin rounded-full border-3 border-red-200 border-t-red-600" />
              <p className="text-sm font-bold text-fg/60">A eliminar conta...</p>
            </div>
          )}
        </ParchmentCard>
      </div>
    </main>
  );
}
